import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API_URL = import.meta.env.VITE_API_URL;

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const { token: tokenParam } = useParams();

  const tokenQuery = searchParams.get("token");
  const token = tokenParam || tokenQuery;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const verificationStarted = useRef(false);

  useEffect(() => {
    if (verificationStarted.current) {
      return;
    }

    verificationStarted.current = true;

    const verifyLoginLink = async () => {
      if (!token) {
        setError("Invalid login link.");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(
          `${API_URL}/api/auth/verify-login-link/${token}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        let data = {};

        try {
          data = await res.json();
        } catch {
          data = {};
        }

        if (!res.ok) {
          setError(
            data.message ||
              "This login link is invalid, expired, or has already been used.",
          );
          setLoading(false);
          return;
        }

        localStorage.setItem("accessToken", data.access);
        localStorage.setItem("refreshToken", data.refresh);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("role", data.user?.role || "");

        const role = data.user?.role;

        if (role === "Admin" || role === "Owner") {
          navigate("/admin", { replace: true });
        } else if (role === "SDA Owner") {
          navigate("/sdaowner/dashboard", { replace: true });
        } else {
          setError("Unauthorized role.");
          setLoading(false);
        }
      } catch (err) {
        console.error("Login link verification error:", err);
        setError("Something went wrong. Please request a new login link.");
        setLoading(false);
      }
    };

    verifyLoginLink();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <Card className="w-full max-w-md shadow-xl rounded-2xl border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold text-[#D2138C]">
            Secure Login
          </CardTitle>

          <CardDescription>
            We&apos;re verifying your secure login link.
          </CardDescription>
        </CardHeader>

        <CardContent className="text-center space-y-5">
          {loading && <p className="text-gray-600">Logging you in...</p>}

          {error && (
            <>
              <p className="text-sm text-red-500">{error}</p>

              <Button
                onClick={() => navigate("/forgotpassword")}
                className="w-full bg-[#D2138C] h-11 rounded-xl text-white hover:bg-[#D2138CE4]">
                Request a new login link
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPassword;
